import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "@helpers/axios/private.axios";
import { Button } from "@components/library";
import { BaseDropdown } from "@components/library/UI";
import { cryptoWithdrawal, fiatWithdrawal } from "@actions";
import { ALERT, WALLET_TYPE } from "@constants";
import { useTranslation } from "@helpers/translate";
import "./index.sass";

const stripAmountFormatting = (value) => String(value ?? "").replace(/,/g, "");

const parseNumber = (value) => {
  const normalized = String(value ?? "")
    .replace(/,/g, "")
    .trim();
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
};

const formatGatewayAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";

  const formatted = n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return `$${formatted}`;
};

const formatCommissionAmount = (value) => {
  const commission = Math.round(parseNumber(value) * 100) / 100;

  if (commission <= 0) {
    return "$0";
  }

  const formatted = commission.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `$${formatted}`;
};

const formatAmountInput = (value) => {
  const stripped = stripAmountFormatting(value).replace(/[^\d.]/g, "");

  if (!stripped) {
    return "";
  }

  const dotIndex = stripped.indexOf(".");
  const intRaw = dotIndex === -1 ? stripped : stripped.slice(0, dotIndex);
  const decRaw =
    dotIndex === -1 ? "" : stripped.slice(dotIndex + 1).replace(/\./g, "");

  const intDigits = intRaw.replace(/\D/g, "");
  const decDigits = decRaw.replace(/\D/g, "").slice(0, 2);

  const formattedInt = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if (dotIndex !== -1) {
    return decDigits.length > 0
      ? `${formattedInt}.${decDigits}`
      : `${formattedInt}.`;
  }

  return formattedInt;
};

const parseAmountForSubmit = (formattedAmount) => {
  const normalized = stripAmountFormatting(formattedAmount).trim();

  if (!normalized) {
    return "";
  }

  const n = Number(normalized);

  return Number.isFinite(n) ? String(n) : normalized;
};

const formatAmountDisplay = (value) => {
  const n = parseNumber(value);
  const normalized = (Math.round(n * 100) / 100).toFixed(2);

  return formatAmountInput(normalized);
};

const mapMethodsWithKind = (methods, kind) =>
  (methods ?? []).map((method) => ({ ...method, kind }));

const getMethodKey = (method) =>
  String(
    method?.method_key ||
      method?.slug ||
      `${method?.kind || method?.type}-${method?.id}`,
  );

const fetchMergedPaymentMethods = async () => {
  const [fiatResp, cryptoResp] = await Promise.all([
    axios.get("/gateway/fiat"),
    axios.get("/gateway/crypto"),
  ]);

  const fiatMethods = mapMethodsWithKind(fiatResp?.data?.data, "fiat");
  const cryptoMethods = mapMethodsWithKind(cryptoResp?.data?.data, "crypto");

  return [...fiatMethods, ...cryptoMethods];
};

const ReadOnlyRow = ({ label, value }) => (
  <div className="post-deposit-withdrawal__row">
    <span className="post-deposit-withdrawal__label">{label}</span>
    <span
      className="post-deposit-withdrawal__value post-deposit-withdrawal__value--text"
      title={value}
    >
      {value}
    </span>
  </div>
);

const FieldRow = ({ label, htmlFor, children }) => (
  <div className="post-deposit-withdrawal__row post-deposit-withdrawal__field">
    <label className="post-deposit-withdrawal__label" htmlFor={htmlFor}>
      {label}
    </label>
    <div className="post-deposit-withdrawal__value">{children}</div>
  </div>
);

export const PostDepositWithdrawal = () => {
  const { __ } = useTranslation();
  const dispatch = useDispatch();
  const { active_wallet } = useSelector((state) => state.wallet);
  const isRealWallet = active_wallet?.type === WALLET_TYPE.REAL_FUTURES;
  const freeBalanceSource = isRealWallet ? active_wallet?.balance : 0;

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [addressError, setAddressError] = useState("");
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadMethods = async () => {
      setMethodsLoading(true);

      try {
        const merged = await fetchMergedPaymentMethods();

        if (cancelled) {
          return;
        }

        setPaymentMethods(merged);

        if (merged.length > 0) {
          setSelectedMethod(merged[0]);
        }
      } catch (error) {
        console.error(error);

        if (!cancelled) {
          setPaymentMethods([]);
          setSelectedMethod(null);
        }
      } finally {
        if (!cancelled) {
          setMethodsLoading(false);
        }
      }
    };

    loadMethods();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedMethodKey = useMemo(
    () => getMethodKey(selectedMethod),
    [selectedMethod],
  );

  const freeBalance = useMemo(
    () => parseNumber(freeBalanceSource ?? 0),
    [freeBalanceSource],
  );

  const freeBalanceDisplay = useMemo(() => {
    const formatted = formatAmountDisplay(freeBalanceSource ?? 0);
    return `$${formatted}`;
  }, [freeBalanceSource]);

  const amountExceedsFreeBalance = useMemo(() => {
    if (!String(amount).trim()) {
      return false;
    }

    const numericAmount = parseNumber(stripAmountFormatting(amount));

    if (numericAmount <= 0) {
      return false;
    }

    return numericAmount > freeBalance;
  }, [amount, freeBalance]);

  const paymentMethodLabel = useMemo(() => {
    if (methodsLoading) {
      return "Loading...";
    }

    if (!selectedMethod) {
      return "No payment methods";
    }

    return selectedMethod.title;
  }, [methodsLoading, selectedMethod]);

  const minimumWithdrawalDisplay = useMemo(() => {
    if (methodsLoading) {
      return "…";
    }

    if (!selectedMethod) {
      return "—";
    }

    return formatGatewayAmount(selectedMethod.withdraw_min);
  }, [methodsLoading, selectedMethod]);

  const commissionDisplay = useMemo(() => {
    if (methodsLoading) {
      return "…";
    }

    if (!selectedMethod || !String(amount).trim()) {
      return "$0";
    }

    const numericAmount = parseNumber(stripAmountFormatting(amount));

    if (numericAmount <= 0) {
      return "$0";
    }

    const withdrawFeePercent = parseNumber(selectedMethod.withdraw_fee);
    const commission = (numericAmount * withdrawFeePercent) / 100;

    return formatCommissionAmount(commission);
  }, [methodsLoading, selectedMethod, amount]);

  const isPaymentMethodDisabled = methodsLoading || paymentMethods.length === 0;

  const isContinueDisabled =
    methodsLoading ||
    !selectedMethod ||
    !String(amount).trim() ||
    !String(address).trim() ||
    amountExceedsFreeBalance ||
    submitting;

  const withdrawal = async () => {
    if (!selectedMethod) {
      return;
    }

    setAddressError("");

    const submitAmount = parseAmountForSubmit(amount);
    const numericAmount = Number(submitAmount);
    const minWithdraw = Number(selectedMethod.withdraw_min);

    if (
      Number.isFinite(minWithdraw) &&
      Number.isFinite(numericAmount) &&
      numericAmount < minWithdraw
    ) {
      const minLabel = formatGatewayAmount(selectedMethod.withdraw_min);
      const message = __("wallet.amount_below_minimum").replace(
        "{min}",
        minLabel,
      );

      dispatch({ type: ALERT.ALERT_ERROR, payload: message });
      return;
    }

    if (numericAmount > freeBalance) {
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload: __("wallet.amount_exceeds_available_balance"),
      });
      return;
    }

    const request = {
      amount: submitAmount,
      bank_account: address,
      id: selectedMethod.id,
      selectedMethodTitle: selectedMethod.title,
      selectedMethodAsset: selectedMethod.asset,
      selectedMethodNetwork: selectedMethod.network,
      selectedMethodSlug: selectedMethod.slug,
      selectedMethodKey: selectedMethod.method_key,
    };

    const dispatchWithdrawal =
      selectedMethod.kind === "fiat"
        ? fiatWithdrawal(request)
        : cryptoWithdrawal(request);

    try {
      setSubmitting(true);

      await axios.post(`/gateway/${selectedMethod.id}/validate`, {
        amount: submitAmount,
        amount_withdrawal: submitAmount,
        bank_account: address,
        method_key: selectedMethod.method_key,
        asset: selectedMethod.asset,
        network: selectedMethod.network,
      });

      await dispatch(dispatchWithdrawal);

      setAmount("");
      setAddress("");
      setAddressError("");
    } catch (error) {
      const responseErrors = error?.response?.data?.errors;
      const addressValidationMessage = responseErrors?.bank_account?.[0];

      if (addressValidationMessage) {
        setAddressError(addressValidationMessage);
      }

      const validationMessage =
        addressValidationMessage ||
        error?.response?.data?.message ||
        Object.values(responseErrors || {})?.[0]?.[0] ||
        __("common.error");

      dispatch({
        type: ALERT.ALERT_ERROR,
        payload: validationMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    withdrawal();
  };

  return (
    <form className="post-deposit-withdrawal-page" onSubmit={handleSubmit}>
      <div className="post-deposit-withdrawal">
        <div className="post-deposit-withdrawal__form">
          <ReadOnlyRow
            label={__("wallet.free_balance")}
            value={freeBalanceDisplay}
          />

          <ReadOnlyRow
            label={__("wallet.minimum_withdrawal_amount")}
            value={minimumWithdrawalDisplay}
          />

          <ReadOnlyRow
            label={__("wallet.commission")}
            value={commissionDisplay}
          />

          <FieldRow
            label={`${__("wallet.payment_method")}:`}
            htmlFor="post-deposit-payment-method"
          >
            <div className="bo-input__wrap post-deposit-withdrawal__method-wrap">
              <BaseDropdown
                className="post-deposit-withdrawal__method-dropdown"
                menuClassName="post-deposit-withdrawal__method-menu"
                useMenuPortal
                matchTriggerWidth
                align="left"
                trigger={
                  <button
                    type="button"
                    id="post-deposit-payment-method"
                    className="bo-input__input post-deposit-withdrawal__select post-deposit-withdrawal__select-trigger"
                    disabled={isPaymentMethodDisabled}
                    data-kind={selectedMethod?.kind ?? ""}
                  >
                    <span className="post-deposit-withdrawal__select-trigger-text">
                      {paymentMethodLabel}
                    </span>

                    <span
                      className="post-deposit-withdrawal__select-chevron"
                      aria-hidden
                    />
                  </button>
                }
              >
                {({ closeDropdown }) => (
                  <div className="post-deposit-withdrawal__method-options">
                    {paymentMethods.map((method) => {
                      const methodKey = getMethodKey(method);
                      const isActive = methodKey === selectedMethodKey;

                      return (
                        <button
                          key={`${method.kind}-${methodKey}`}
                          type="button"
                          className={`post-deposit-withdrawal__method-option${
                            isActive
                              ? " post-deposit-withdrawal__method-option--active"
                              : ""
                          }`}
                          onClick={() => {
                            setSelectedMethod(method);
                            setAddressError("");
                            closeDropdown();
                          }}
                        >
                          {method.title}
                        </button>
                      );
                    })}
                  </div>
                )}
              </BaseDropdown>
            </div>
          </FieldRow>

          <FieldRow
            label={`${__("wallet.deposit.amount")}:`}
            htmlFor="post-deposit-amount"
          >
            <div className="bo-input__wrap">
              <div className="post-deposit-withdrawal__amount-input">
                <span className="post-deposit-withdrawal__amount-prefix">
                  $
                </span>
                <span
                  className="post-deposit-withdrawal__amount-divider"
                  aria-hidden="true"
                />

                <input
                  id="post-deposit-amount"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder=""
                  className="bo-input__input post-deposit-withdrawal__amount-field"
                  value={amount}
                  onChange={(event) => {
                    setAmount(formatAmountInput(event.target.value));
                  }}
                />
              </div>
            </div>
            {amountExceedsFreeBalance && (
              <p className="post-deposit-withdrawal__amount-error">
                {__("wallet.withdrawal.amount_exceeds_balance")}
              </p>
            )}
          </FieldRow>

          <FieldRow
            label={__("wallet.base_network_usdc_address")}
            htmlFor="post-deposit-address"
          >
            <div className="bo-input__wrap">
              <input
                id="post-deposit-address"
                type="text"
                placeholder=""
                className={`bo-input__input ${addressError ? "error" : ""}`}
                value={address}
                onChange={(event) => {
                  setAddress(event.target.value);
                  setAddressError("");
                }}
              />
            </div>

            {addressError && (
              <p className="post-deposit-withdrawal__amount-error">
                {addressError}
              </p>
            )}
          </FieldRow>
        </div>
      </div>

      <div className="post-deposit-withdrawal__actions">
        <Button
          type="submit"
          disabled={isContinueDisabled}
          className="post-deposit-withdrawal__btn"
          color="orange"
          size="middle"
        >
          {__("wallet.continue")}
        </Button>
      </div>
    </form>
  );
};

export default PostDepositWithdrawal;